import {
  CheckSquare,
  CircleDot,
  Crosshair,
  FileText,
  ListOrdered,
  Shuffle,
  ToggleLeft,
  PenLine,
  Boxes,
  Table2,
  TextCursorInput,
  ListChecks,
  type LucideIcon,
} from 'lucide-react';
import { TRAINING_UNMARKED_ONLY_PAGE_TYPES, type TrainingPageType } from '@hcc/types';

/** Display metadata per page type, shared by the add menu and the sidebar. */
export const PAGE_META: Record<
  TrainingPageType,
  { label: string; icon: LucideIcon; description: string }
> = {
  slide: { label: 'Content slide', icon: FileText, description: 'Text, media and layout' },
  question_group: {
    label: 'Question grid',
    icon: Table2,
    description: 'Several questions, shared answer columns',
  },
  single_choice: {
    label: 'Single choice',
    icon: CircleDot,
    description: 'Pick one correct answer',
  },
  multi_select: { label: 'Multiple select', icon: CheckSquare, description: 'Pick all that apply' },
  true_false: { label: 'True or false', icon: ToggleLeft, description: 'A true/false statement' },
  short_text: { label: 'Fill in the blank', icon: PenLine, description: 'Type the missing answer' },
  categorize: { label: 'Sort into groups', icon: Boxes, description: 'Sort items into buckets' },
  ordering: { label: 'Put in order', icon: ListOrdered, description: 'Arrange in the right order' },
  hotspot: {
    label: 'Click the picture',
    icon: Crosshair,
    description: 'Tap the right spot on an image',
  },
  matching: { label: 'Match the pairs', icon: Shuffle, description: 'Match left to right' },
  text_group: {
    label: 'Questions and answers',
    icon: TextCursorInput,
    description: 'Several questions, a text box each',
  },
  follow_up: {
    label: 'Follow-up questions',
    icon: ListChecks,
    description: 'Explain every answer that matched, e.g. each "Yes"',
  },
};

/** Grouping for the visual add-page menu (templates/pictures handled separately). */
export const PAGE_GROUPS: { title: string; types: TrainingPageType[] }[] = [
  { title: 'Content', types: ['slide'] },
  {
    title: 'Questions',
    types: [
      'question_group',
      'single_choice',
      'multi_select',
      'true_false',
      'short_text',
      'categorize',
      'ordering',
      'hotspot',
      'matching',
    ],
  },
  { title: 'Assessment only (not marked)', types: [...TRAINING_UNMARKED_ONLY_PAGE_TYPES] },
];

/** The add-menu groups an author may use: unmarked-only pages hide in a marked module. */
export function pageGroupsFor(graded: boolean): { title: string; types: TrainingPageType[] }[] {
  if (!graded) return PAGE_GROUPS;
  const barred = new Set<string>(TRAINING_UNMARKED_ONLY_PAGE_TYPES);
  return PAGE_GROUPS.map((g) => ({ ...g, types: g.types.filter((t) => !barred.has(t)) })).filter(
    (g) => g.types.length > 0,
  );
}

export const SHARED_INPUT_CLASS =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';
